// Canonical PDV master product helpers. No external publication, no invented claims.
import {isValidGtin,normalizeBarcode} from './domain.js';

const norm=value=>String(value||'').normalize('NFD').replace(/[\u0300-\u036f]/g,'')
  .toLocaleLowerCase('pt-BR').replace(/[^a-z0-9]+/g,' ').trim();

export function proposeClassification(suggestion={},productName=''){
  const incoming=String(suggestion.category||'').trim();
  const sub=String(suggestion.subcategory||'').trim();
  const descriptor=norm([incoming,sub,productName||suggestion.name].join(' '));
  // Specific family is a subcategory, not a new top-level taxon.
  if(/\bagua(s)? mineral(is)?\b/.test(descriptor)){
    return {category:'Bebidas',subcategory:'Água mineral',basis:'product_type'};
  }
  // Suggest the retail family rather than using "confeitaria" as an
  // unsubstantiated local usage attribute.
  if(/\bmistura (?:para|de) bolo\b/.test(descriptor)){
    return {category:'Alimentos',subcategory:'Misturas para bolo',basis:'product_type'};
  }
  if(/\b(?:bolo|bolinho|bolos|bolinhos)\b/.test(descriptor)){
    return {category:'Alimentos',subcategory:'Bolos e bolinhos',basis:'product_type'};
  }
  return {category:incoming,subcategory:sub,basis:sub?'visual_suggestion':'category_suggestion'};
}

export function findExistingProducts(products=[],candidate={},excludeId=''){
  const barcode=normalizeBarcode(candidate?.barcode||'');
  const sku=norm(candidate?.sku);
  const name=norm(candidate?.name);
  const brand=norm(candidate?.brand);
  const scored=[];
  for(const product of products){
    if(product.id===excludeId)continue;
    const sameBarcode=barcode&&normalizeBarcode(product.barcode)===barcode;
    const sameSku=sku&&norm(product.sku)===sku;
    const sameName=name&&name===norm(product.name)&&(!brand||brand===norm(product.brand));
    if(sameBarcode||sameSku||sameName){
      scored.push({product,reason:sameBarcode?'barcode':sameSku?'sku':'name_brand',
        confidence:sameBarcode||sameSku?'exact_identifier':'review'});
    }
  }
  return scored.slice(0,5);
}

export function productReadiness(product={},channels=[],entitlement={},options={}){
  const commerce=product.metadata?.commerce||{};
  const variants=product.metadata?.media?.variants||[];
  const approvedVariant=Array.isArray(variants)&&variants.some(v=>
    v.status==='approved'&&['cutout','white_background','brand_background','marketplace_square'].includes(v.kind));
  const baseMissing=[];
  if(!product.name?.trim())baseMissing.push('nome');
  if(!product.image_path)baseMissing.push(options.stagedPhoto?'foto selecionada (salvar primeiro)':'foto');
  if(!product.price_cents||product.price_cents<=0)baseMissing.push('preço');
  const onlineMissing=[...baseMissing];
  if(!commerce.online_title)onlineMissing.push('título online');
  if(!commerce.online_description)onlineMissing.push('descrição online');
  if(!commerce.shipping_weight_g)onlineMissing.push('peso para entrega');
  if(!commerce.shipping_dimensions_cm)onlineMissing.push('dimensões');
  const normalizedChannels=(Array.isArray(channels)?channels:[]).map(c=>({
    provider:norm(c.provider),status:norm(c.status)
  }));
  const connected=(terms)=>normalizedChannels.some(c=>
    terms.some(t=>c.provider.includes(t))&&['connected','active','authorized'].includes(c.status));
  const make=(key,title,missing,connector=false,requiresCutout=false)=>({
    key,title,
    data_complete:missing.length===0,
    missing,
    channel_connected:connector,
    status:missing.length?'missing_data':!connector?'not_connected':requiresCutout&&!approvedVariant?'media_review_required':'ready_for_review',
    publishable:false // Publish/channel gate is NEVER inferred from catalog readiness.
  });
  const hasPremium=entitlement?.media_features?.includes?.('cutout')===true;
  return {
    pdv:{key:'pdv',title:'PDV',status:baseMissing.length?'missing_data':'ready_for_review',
      missing:baseMissing,publishable:false},
    sales:make('sales','Kaizen Sales',baseMissing,connected(['sales'])),
    publish:make('publish','Kaizen Publish',onlineMissing,connected(['instagram','publish'])),
    google:make('google','Google Merchant',[
      ...onlineMissing,...(!isValidGtin(product.barcode||'')?['GTIN confirmado']:[])
    ],connected(['google','merchant']),true),
    whatsapp:make('whatsapp','WhatsApp catálogo',onlineMissing,connected(['whatsapp']),true),
    marketplace:make('marketplace','Marketplaces',onlineMissing,false,true),
    image:{status:approvedVariant?'approved':!product.image_path&&options.stagedPhoto?'pending_save':hasPremium?'requires_processing':'original_only',
      original:!!product.image_path,staged:!!options.stagedPhoto,approved_variant:approvedVariant,premium_eligible:hasPremium}
  };
}
